import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import en from './en.json';
import fr from './fr.json';

/**
 * FND-7.3 / uml/17-project-scaffolding.md S18, NFR04.
 *
 * **The rule: a string is added to BOTH files in the same commit that uses it.**
 * It costs nothing now. A bilingual pass in week 4 over two hundred strings
 * costs a day, and that day is in the week with no slack.
 *
 * `saveMissing` is off but `missingKeyHandler` still fires in development, so a
 * key added to en.json and forgotten in fr.json shows up in the console the
 * first time the French shell renders it - which is how the rule above stays
 * true without anyone policing it.
 */

export const SUPPORTED_LANGUAGES = ['fr', 'en'] as const;
export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

const STORAGE_KEY = 'myscaw.lang';

/**
 * French first: ISCAE Casablanca teaches in French, so it is the default for a
 * visitor with no stored choice and no matching browser preference.
 */
export const DEFAULT_LANGUAGE: SupportedLanguage = 'fr';

function detectLanguage(): SupportedLanguage {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupported(stored)) return stored;
  } catch {
    // Private browsing, or storage disabled. Fall through to the browser.
  }

  const browser = window.navigator.language.slice(0, 2).toLowerCase();
  return isSupported(browser) ? browser : DEFAULT_LANGUAGE;
}

function isSupported(value: string | null): value is SupportedLanguage {
  return value !== null && (SUPPORTED_LANGUAGES as readonly string[]).includes(value);
}

export function setLanguage(language: SupportedLanguage): void {
  void i18n.changeLanguage(language);
  try {
    window.localStorage.setItem(STORAGE_KEY, language);
  } catch {
    // The choice still applies to this tab; it just will not survive a reload.
  }
  // Drives hyphenation, spell-check and how a screen reader pronounces the page.
  document.documentElement.lang = language;
}

/** omar's `shared/LanguageSwitcher.tsx` calls it by this name. One implementation. */
export const loadLanguage = (language: SupportedLanguage): void => setLanguage(language);

void i18n.use(initReactI18next).init({
  resources: {
    en: { translation: en },
    fr: { translation: fr },
  },
  lng: detectLanguage(),
  fallbackLng: DEFAULT_LANGUAGE,
  interpolation: {
    // React escapes for us; doing it twice turns an apostrophe into &#39;
    // in French, where apostrophes are everywhere.
    escapeValue: false,
  },
  missingKeyHandler: (languages, _namespace, key) => {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] missing key "${key}" for ${languages.join(', ')}`);
    }
  },
  saveMissing: import.meta.env.DEV,
});

document.documentElement.lang = i18n.language;

export default i18n;
